/**
 * Journal i18n — lightweight, dependency-free React i18n.
 *
 * Why homegrown (no i18next): the journal has a few hundred short UI strings
 * and no server-driven content, so a typed dictionary + a tiny `t()` with
 * `{var}` interpolation covers everything without shipping a library. Locale is
 * persisted to localStorage ('journal.lang') and drives BOTH the copy and the
 * document direction (fa/ar/ur → rtl), applied to <html> before React mounts to
 * avoid a flash of the wrong direction/font (same pattern as lib/theme.ts).
 *
 * Keys are dotted + namespaced by page/area (e.g. "nav.dashboard",
 * "dash.welcome"). Lookups fall back current-lang → en → the key itself, so a
 * missing translation degrades to English rather than showing a raw key.
 *
 * LOADING: English is bundled (it is the fallback, so it is always needed);
 * every other locale is a dynamic import and becomes its own chunk. With 27
 * languages × ~570 strings, static imports would have made every user download
 * every dictionary to read one — roughly doubling the bundle.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { en } from "./en";

export type Lang =
  | "en" | "zh" | "hi" | "es" | "ar" | "fr" | "bn" | "pt" | "ru" | "ur"
  | "id" | "de" | "ja" | "mr" | "te" | "tr" | "ta" | "vi" | "ko" | "fa"
  | "sw" | "ha" | "it" | "pl" | "uk" | "th" | "gu";

/** `label` is the language's own endonym — never its English name. */
export const LANGS: { code: Lang; label: string; dir: "ltr" | "rtl" }[] = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "hi", label: "हिन्दी", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "bn", label: "বাংলা", dir: "ltr" },
  { code: "pt", label: "Português", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "ur", label: "اردو", dir: "rtl" },
  { code: "id", label: "Bahasa Indonesia", dir: "ltr" },
  { code: "de", label: "Deutsch", dir: "ltr" },
  { code: "ja", label: "日本語", dir: "ltr" },
  { code: "mr", label: "मराठी", dir: "ltr" },
  { code: "te", label: "తెలుగు", dir: "ltr" },
  { code: "tr", label: "Türkçe", dir: "ltr" },
  { code: "ta", label: "தமிழ்", dir: "ltr" },
  { code: "vi", label: "Tiếng Việt", dir: "ltr" },
  { code: "ko", label: "한국어", dir: "ltr" },
  { code: "fa", label: "فارسی", dir: "rtl" },
  { code: "sw", label: "Kiswahili", dir: "ltr" },
  { code: "ha", label: "Hausa", dir: "ltr" },
  { code: "it", label: "Italiano", dir: "ltr" },
  { code: "pl", label: "Polski", dir: "ltr" },
  { code: "uk", label: "Українська", dir: "ltr" },
  { code: "th", label: "ไทย", dir: "ltr" },
  { code: "gu", label: "ગુજરાતી", dir: "ltr" },
];

const LANG_CODES = LANGS.map((l) => l.code);

/**
 * One dynamic import per locale. Written out longhand rather than built from a
 * template string so the bundler can statically see every chunk — a computed
 * `import(`./${lang}`)` would make Vite bundle the whole directory eagerly,
 * defeating the point.
 */
const LOADERS: Record<Exclude<Lang, "en">, () => Promise<Record<string, string>>> = {
  zh: () => import("./zh").then((m) => m.zh),
  hi: () => import("./hi").then((m) => m.hi),
  es: () => import("./es").then((m) => m.es),
  ar: () => import("./ar").then((m) => m.ar),
  fr: () => import("./fr").then((m) => m.fr),
  bn: () => import("./bn").then((m) => m.bn),
  pt: () => import("./pt").then((m) => m.pt),
  ru: () => import("./ru").then((m) => m.ru),
  ur: () => import("./ur").then((m) => m.ur),
  id: () => import("./id").then((m) => m.id),
  de: () => import("./de").then((m) => m.de),
  ja: () => import("./ja").then((m) => m.ja),
  mr: () => import("./mr").then((m) => m.mr),
  te: () => import("./te").then((m) => m.te),
  tr: () => import("./tr").then((m) => m.tr),
  ta: () => import("./ta").then((m) => m.ta),
  vi: () => import("./vi").then((m) => m.vi),
  ko: () => import("./ko").then((m) => m.ko),
  fa: () => import("./fa").then((m) => m.fa),
  sw: () => import("./sw").then((m) => m.sw),
  ha: () => import("./ha").then((m) => m.ha),
  it: () => import("./it").then((m) => m.it),
  pl: () => import("./pl").then((m) => m.pl),
  uk: () => import("./uk").then((m) => m.uk),
  th: () => import("./th").then((m) => m.th),
  gu: () => import("./gu").then((m) => m.gu),
};

/** Dictionaries loaded so far. English is present from the first render. */
const DICTS: Partial<Record<Lang, Record<string, string>>> = { en };

const RTL: ReadonlySet<Lang> = new Set<Lang>(["ar", "fa", "ur"]);
const KEY = "journal.lang";

export function isRtl(lang: Lang): boolean {
  return RTL.has(lang);
}

function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (LANG_CODES as string[]).includes(v);
}

export function getInitialLang(): Lang {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem(KEY);
    if (isLang(saved)) return saved;
  } catch {
    /* ignore */
  }
  // Match the browser's preference on the base subtag, so "pt-BR" → pt and
  // "zh-Hans-CN" → zh.
  const nav = (typeof navigator !== "undefined" && navigator.language) || "";
  const base = nav.toLowerCase().split("-")[0];
  return isLang(base) ? base : "en";
}

/**
 * Load a locale's dictionary, caching it. Resolves immediately when already
 * loaded so switching back to a language is instant.
 */
export async function loadLang(lang: Lang): Promise<void> {
  if (lang === "en" || DICTS[lang]) return;
  try {
    DICTS[lang] = await LOADERS[lang]();
  } catch {
    // A failed chunk (offline, bad deploy) must not break the app — English
    // stays in place and the UI simply renders untranslated.
  }
}

/** Set <html lang/dir> + the font class. Call before mount and on change. */
export function applyLangDir(lang: Lang): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.setAttribute("lang", lang);
  el.setAttribute("dir", isRtl(lang) ? "rtl" : "ltr");
  el.classList.remove("font-latin", "font-fa", "font-ar");
  // fa has its own bundled face; ar and ur share the Arabic-script stack.
  el.classList.add(
    lang === "fa" ? "font-fa" : lang === "ar" || lang === "ur" ? "font-ar" : "font-latin",
  );
}

export function setStoredLang(lang: Lang): void {
  try {
    window.localStorage.setItem(KEY, lang);
  } catch {
    /* ignore */
  }
}

/** Look up + interpolate `{var}` placeholders. Exported for non-hook contexts. */
export function translate(lang: Lang, key: string, vars?: Record<string, string | number>): string {
  const raw = DICTS[lang]?.[key] ?? en[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

type TFn = (key: string, vars?: Record<string, string | number>) => string;

const I18nContext = createContext<{ lang: Lang; setLang: (l: Lang) => void; t: TFn; dir: "ltr" | "rtl" }>({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
  dir: "ltr",
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);
  // Bumped once a dictionary finishes loading, to re-render with the new copy.
  const [revision, setRevision] = useState(0);

  // First paint uses the stored language, whose dictionary is not loaded yet.
  useEffect(() => {
    if (DICTS[lang]) return;
    let cancelled = false;
    loadLang(lang).then(() => {
      if (!cancelled) setRevision((r) => r + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setStoredLang(l);
    applyLangDir(l);
    // Fetch the dictionary BEFORE switching, so the UI never flashes English
    // on its way to the chosen language.
    loadLang(l).then(() => setLangState(l));
  }, []);

  const t = useCallback<TFn>(
    (key, vars) => translate(lang, key, vars),
    // `revision` participates so components re-render when a dictionary lands.
    [lang, revision],
  );

  const value = useMemo(
    () => ({ lang, setLang, t, dir: (isRtl(lang) ? "rtl" : "ltr") as "ltr" | "rtl" }),
    [lang, setLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

/** Convenience: just the `t` function. */
export function useT(): TFn {
  return useContext(I18nContext).t;
}
